import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { extractErrorMessage, useStore } from "../../../helpers";
import { uploadBlob } from "../../../helpers/file-upload";
import { TARS_SOURCES_BUCKET } from "../notebook-details/sidebar/add-source/addSourceUtils";
import { TARS_FACETS_S3_PREFIX, TARS_FACETS_UPDATE_PATH } from "../tars-constants";
import { markTarsNotebookActivity } from "./useGetNotebooks";
import { safe_get } from "../../../report-visuals";

export interface UpdateFacetPayload {
  facet_id: number;
  notebook_id?: number;
  etl_json: Record<string, unknown>;
  facet_result: Record<string, unknown>;
  version: number;
  more: Record<string, unknown>;
  s3_key?: string;
}

function sanitizeFacetS3Name(name: string): string {
  const cleaned = name
    .trim()
    .replace(/[/\\?%*:|"<>#]/g, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^\.+|\.+$/g, "");
  return cleaned || "facet";
}

function resolveFacetName(payload: UpdateFacetPayload): string {
  const fromMore = safe_get(payload.more, "name", undefined);
  if (typeof fromMore === "string" && fromMore.trim()) return fromMore.trim();
  const fromEtl = safe_get(payload.etl_json, "etl.name", undefined);
  if (typeof fromEtl === "string" && fromEtl.trim()) return fromEtl.trim();
  return `facet_${payload.facet_id}`;
}

async function withFacetUpdateS3(payload: UpdateFacetPayload): Promise<UpdateFacetPayload> {
  const { facet_id: facetId, notebook_id: notebookId } = payload;
  if (!notebookId || notebookId <= 0 || !facetId || facetId <= 0) return payload;

  const facetName = sanitizeFacetS3Name(resolveFacetName(payload));
  const dir = `${TARS_FACETS_S3_PREFIX}/${notebookId}/facets`;
  const filename = `${facetName}.json`;
  const json = JSON.stringify(
    {
      facet_id: payload.facet_id,
      etl_json: payload.etl_json,
      facet_result: payload.facet_result,
      version: payload.version,
      more: payload.more ?? {},
    },
    null,
    2
  );
  const uploaded = await uploadBlob(
    dir,
    filename,
    new Blob([json], { type: "application/json" }),
    TARS_SOURCES_BUCKET
  );
  if (!uploaded) throw new Error("Failed to upload facet update to S3");

  const s3Key = `${dir}/${filename}`;
  return {
    facet_id: payload.facet_id,
    notebook_id: payload.notebook_id,
    version: payload.version,
    etl_json: {},
    facet_result: {},
    s3_key: s3Key,
    more: { ...(payload.more ?? {}), s3_key: s3Key },
  };
}

export const useUpdateFacet = () => {
  const rootStore = useStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateFacetPayload) => {
      const body = await withFacetUpdateS3(payload);
      const res = await rootStore.apiRequestHandler(
        TARS_FACETS_UPDATE_PATH,
        body,
        "Failed to update facet"
      );
      return res.data ?? res;
    },
    onSuccess: (_data, variables) => {
      markTarsNotebookActivity(queryClient, variables.notebook_id);
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, "Failed to update facet"));
    },
  });
};
